import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Header from "../components/Header";
import { useAuth } from "../lib/auth";
import { api, apiErr } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { CheckCircle2, Loader2, ExternalLink } from "lucide-react";

/**
 * Handles the return from Razorpay Payment Link.
 * If Razorpay redirects with query params razorpay_payment_id + razorpay_payment_link_id,
 * we auto-activate. Otherwise we ask the user to paste their payment ID.
 */
export default function SubscriptionSuccess() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { refreshUser } = useAuth();

  const orderId = params.get("order_id") || sessionStorage.getItem("pending_order_id") || "";
  const razorpayPaymentId = params.get("razorpay_payment_id") || "";
  const razorpayPaymentLinkId = params.get("razorpay_payment_link_id") || "";

  const [status, setStatus] = useState("idle"); // idle | activating | done | error
  const [manualPaymentId, setManualPaymentId] = useState("");

  const activate = async (paymentId) => {
    if (!orderId) {
      toast.error("Missing order info — please retry from the Subscribe page.");
      return;
    }
    setStatus("activating");
    try {
      await api.post("/subscription/confirm-payment", null, {
        params: {
          order_id: orderId,
          razorpay_payment_id: paymentId || "",
          razorpay_payment_link_id: razorpayPaymentLinkId || "",
        },
      });
      await refreshUser();
      sessionStorage.removeItem("pending_order_id");
      setStatus("done");
      toast.success("Subscription activated!");
    } catch (e) {
      toast.error(apiErr(e));
      setStatus("error");
    }
  };

  // Auto-activate if Razorpay sent us the payment id
  useEffect(() => {
    if (razorpayPaymentId && orderId && status === "idle") {
      activate(razorpayPaymentId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [razorpayPaymentId, orderId]);

  return (
    <div className="min-h-screen">
      <Header />
      <div className="max-w-lg mx-auto px-4 py-16">
        <div className="brut-card p-8 bg-white">
          {status === "done" ? (
            <div className="text-center">
              <div className="w-16 h-16 mx-auto brut-border bg-[#B8E6C6] flex items-center justify-center">
                <CheckCircle2 size={36} strokeWidth={2.5}/>
              </div>
              <h1 className="text-3xl font-black mt-4">Subscription active!</h1>
              <p className="mt-2 text-neutral-700">You're all set. Enjoy full access to BrimiAI · ScienceLab.</p>
              <Button data-testid="go-dashboard-btn" onClick={() => nav("/dashboard")}
                className="brut-btn rounded-none w-full mt-6 bg-[#1A1A1A] text-white hover:bg-[#1A1A1A] py-6">
                Go to Dashboard
              </Button>
            </div>
          ) : status === "activating" ? (
            <div className="text-center py-10">
              <Loader2 size={40} className="animate-spin mx-auto" strokeWidth={2.5}/>
              <div className="mt-4 font-bold">Activating your subscription…</div>
            </div>
          ) : (
            <>
              <h1 className="text-2xl font-black">Confirm your payment</h1>
              <p className="mt-2 text-sm text-neutral-700">
                If Razorpay didn't redirect you automatically, paste the
                <b> Payment ID</b> from your Razorpay confirmation page below (it
                starts with <code>pay_...</code>) and click Activate.
              </p>
              <Input
                data-testid="manual-payment-id"
                value={manualPaymentId}
                onChange={(e) => setManualPaymentId(e.target.value)}
                placeholder="pay_XXXXXXXXXX"
                className="brut-input rounded-none mt-4"
              />
              <Button
                data-testid="activate-btn"
                onClick={() => activate(manualPaymentId)}
                disabled={!orderId}
                className="brut-btn rounded-none w-full mt-4 bg-[#1A1A1A] text-white hover:bg-[#1A1A1A] py-5">
                Activate Subscription
              </Button>
              <div className="text-xs text-neutral-500 mt-3">
                Order ref: {orderId || "—"}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
